import React, { useEffect, useState } from "react";
import { X, Download, FileText, AlertCircle, ExternalLink } from "lucide-react";

import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { appealApi } from "../../../api/appealApi";

const AttachmentPreviewDialog = ({ open, attachment, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [downloadingState, setDownloadingState] = useState(false);

  const attachmentId = attachment?.AppealAttachmentId;
  const fileName = attachment?.OriginalFileName || "attachment";

  const contentType = (attachment?.ContentType || "").toLowerCase();

  const isPdf =
    contentType.includes("pdf") || fileName.toLowerCase().endsWith(".pdf");

  const isImage = contentType.startsWith("image/");

  useEffect(() => {
    if (!open || !attachmentId) {
      setFileUrl("");
      setLoading(false);
      setError("");
      return;
    }

    let objectUrl = null;
    let cancelled = false;

    const loadFile = async () => {
      setLoading(true);
      setError("");
      setFileUrl("");

      try {
        const res = await appealApi.getAttachmentFile(attachmentId);

        if (cancelled) return;

        const blob =
          res.data instanceof Blob
            ? res.data
            : new Blob([res.data], {
                type: attachment?.ContentType || "application/octet-stream",
              });

        const previewBlob = new Blob([blob], {
          type:
            attachment?.ContentType || blob.type || "application/octet-stream",
        });

        objectUrl = URL.createObjectURL(previewBlob);
        setFileUrl(objectUrl);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to preview attachment:", err);
          setError("Unable to preview this attachment.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadFile();

    return () => {
      cancelled = true;

      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [open, attachmentId, attachment?.ContentType]);

  useEffect(() => {
    setDownloadingState(false);
  }, [open, attachmentId]);

  if (!open || !attachment) {
    return null;
  }

  const handleDownload = () => {
    if (!fileUrl || downloadingState) return;

    setDownloadingState(true);

    try {
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = fileName;
      link.style.display = "none";

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Failed to download attachment:", err);
    } finally {
      window.setTimeout(() => {
        setDownloadingState(false);
      }, 1000);
    }
  };

  const handleOpenPdf = () => {
    if (!fileUrl) return;

    const newWindow = window.open(fileUrl, "_blank");

    if (newWindow) {
      newWindow.opener = null;
    }
  };

  const buttonStyle = {
    padding: "7px 10px",
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 5,
    whiteSpace: "nowrap",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(15,23,42,.65)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 12,
      }}
      data-cy="attachmentPreviewDialog"
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1100,
          height: "90vh",
          height: "90dvh",
          background: "#fff",
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,.3)",
          display: "flex",
          flexDirection: "column",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            minHeight: 58,
            padding: "10px 12px 10px 16px",
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <div
            style={{
              minWidth: 0,
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 9,
            }}
          >
            <FileText size={18} color="#0D9488" style={{ flexShrink: 0 }} />

            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#334155",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={fileName}
            >
              {fileName}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              flexShrink: 0,
            }}
          >
            {fileUrl && isPdf && (
              <button
                type="button"
                onClick={handleOpenPdf}
                data-cy="open-pdf-button"
                style={{
                  ...buttonStyle,
                  border: "1px solid #CBD5E1",
                  background: "#FFFFFF",
                  color: "#334155",
                }}
              >
                <ExternalLink size={13} />
                <span>Open PDF</span>
              </button>
            )}

            {fileUrl && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloadingState}
                data-cy="download-attachment-button"
                style={{
                  ...buttonStyle,
                  border: "1px solid rgba(13,148,136,.2)",
                  background: "rgba(13,148,136,.08)",
                  color: "#0D9488",
                  opacity: downloadingState ? 0.7 : 1,
                }}
              >
                <Download size={13} />
                              <span>{downloadingState ? "Downloading..." : "Download"}</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              data-cy="close-attachment-button"
              aria-label="Close attachment preview"
              style={{
                width: 34,
                height: 34,
                border: "none",
                borderRadius: 8,
                background: "#F3F4F6",
                color: "#64748B",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <X size={18} />
            </button>
          </div>
        </div>
        {/* Preview Area */}
        <div
          style={{
            flex: 1,
            minHeight: 0,
            background: "#F1F5F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "auto",
          }}
        >
          {/* Loading */}
          {loading && (
            <div
              style={{
                padding: 40,
                textAlign: "center",
              }}
            >
              <LoadingSpinner message="Loading preview..." />
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div
              style={{
                padding: 20,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                color: "#B91C1C",
                textAlign: "center",
              }}
            >
              <AlertCircle size={28} />

              <div style={{ fontSize: 13 }}>{error}</div>

              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: "8px 13px",
                  borderRadius: 8,
                  border: "none",
                  background: "#0D9488",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          )}

          {/* PDF Preview */}
          {!loading && !error && fileUrl && isPdf && (
            <iframe
              src={fileUrl}
              title={fileName}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
                background: "#fff",
              }}
              allow="fullscreen"
            />
          )}

          {/* Image Preview */}
          {!loading && !error && fileUrl && isImage && (
            <img
              src={fileUrl}
              alt={fileName}
              style={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                padding: 16,
                boxSizing: "border-box",
              }}
            />
          )}

          {/* Unsupported File */}
          {!loading && !error && fileUrl && !isPdf && !isImage && (
            <div
              style={{
                padding: 30,
                textAlign: "center",
              }}
            >
              <FileText size={50} color="#94A3B8" />

              <div
                style={{
                  marginTop: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  color: "#475569",
                }}
              >
                Preview is not available
              </div>

              <div
                style={{
                  marginTop: 5,
                  fontSize: 12,
                  color: "#94A3B8",
                }}
              >
                Download the file to open it.
              </div>

              <button
                type="button"
                onClick={handleDownload}
                disabled={downloadingState}
                data-cy="download-attachment-button"
                style={{
                  marginTop: 15,
                  padding: "8px 13px",
                  borderRadius: 8,
                  border: "none",
                  background: "#0D9488",
                  color: "#fff",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Download size={14} />

                {downloadingState ? "Preparing..." : "Download"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AttachmentPreviewDialog;
